import api from '@/lib/axios';

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'textarea', 'dropdown', 'radio', 'checkbox', 'file'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  textarea: 'Long text',
  dropdown: 'Dropdown',
  radio: 'Radio (single choice)',
  checkbox: 'Checkbox (multi choice)',
  file: 'File upload',
};

export const OPTION_TYPES: ReadonlySet<CustomFieldType> = new Set(['dropdown', 'radio', 'checkbox']);

export interface CustomField {
  id: string;
  legacyId: number | null;
  name: string;
  type: CustomFieldType;
  isRequired: boolean;
  options: string[] | null;
  rank: number;
  deletedAt: string | null;
}

export interface CreateCustomFieldInput {
  name: string;
  type: CustomFieldType;
  isRequired?: boolean;
  options?: string[];
}

export interface UpdateCustomFieldInput {
  name?: string;
  isRequired?: boolean;
  options?: string[];
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body = r.data as any;
  return body && typeof body === 'object' && 'data' in body ? body.data : (body as T);
}

export const CustomFieldsApi = {
  listActive: async (): Promise<CustomField[]> => unwrap(await api.get('/custom-fields/active')),

  list: async (params: { search?: string; showDeleted?: boolean }): Promise<CustomField[]> =>
    unwrap(await api.get('/custom-fields', { params })),

  create: async (data: CreateCustomFieldInput): Promise<CustomField> =>
    unwrap(await api.post('/custom-fields', data)),

  update: async (id: string, data: UpdateCustomFieldInput): Promise<CustomField> =>
    unwrap(await api.patch(`/custom-fields/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/custom-fields/${id}`)),

  restore: async (id: string): Promise<void> => unwrap(await api.post(`/custom-fields/${id}/restore`)),

  hardDelete: async (id: string): Promise<void> => unwrap(await api.delete(`/custom-fields/${id}/permanent`)),

  reorder: async (ids: string[]): Promise<void> => unwrap(await api.patch('/custom-fields/reorder', { ids })),
};
